import React, { useState, useRef } from 'react';
import { Text } from '@radix-ui/themes';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Input = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  error,
  required = false,
  disabled = false,
  icon,
  showPasswordToggle = false,
  className = '',
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);
  const isPassword = type === 'password';
  const hasPasswordToggle = isPassword && showPasswordToggle;
  const inputType = isPassword && showPasswordToggle ? (showPassword ? 'text' : 'password') : type;

  // Safari fails to repaint a focused input when its type flips between
  // password and text — blur, let React swap the type, then restore focus.
  const togglePasswordReveal = () => {
    const el = inputRef.current;
    const hadFocus = el && document.activeElement === el;
    if (hadFocus) el.blur();
    setShowPassword((prev) => !prev);
    if (hadFocus) {
      requestAnimationFrame(() => {
        el.focus({ preventScroll: true });
        const pos = el.value.length;
        try { el.setSelectionRange(pos, pos); } catch (_) { /* not all types support selection */ }
      });
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <Text
          as="label"
          htmlFor={name}
          size="2"
          weight="medium"
          style={{ display: 'block', marginBottom: '4px', color: '#1f1f1f', textAlign: 'left' }}
        >
          {label}
          {required && <span style={{ color: '#e5484d', marginLeft: '3px' }}>*</span>}
        </Text>
      )}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {icon && (
          <div style={{ position: 'absolute', left: '12px', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
            {icon}
          </div>
        )}
        <input
          ref={inputRef}
          id={name}
          name={name}
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          style={{
            width: '100%',
            padding: '10px 12px',
            paddingLeft: icon ? '40px' : '12px',
            paddingRight: hasPasswordToggle ? '44px' : '12px',
            minHeight: '44px',
            borderRadius: '6px',
            border: error ? '1px solid #e5484d' : '1px solid var(--gray-6)',
            backgroundColor: 'transparent',
            color: '#1f1f1f',
            fontSize: '16px',
            outline: 'none',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#f05a35';
            e.target.style.boxShadow = '0 0 0 2px rgba(255,120,0,0.25)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = error ? '#e5484d' : 'var(--gray-6)';
            e.target.style.boxShadow = 'none';
          }}
          {...props}
        />
        {hasPasswordToggle && (
          <button
            type="button"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={togglePasswordReveal}
            style={{ 
              position: 'absolute', 
              right: '8px', 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              padding: '0',
            }}
          >
            {showPassword
              ? <EyeSlashIcon style={{ width: '18px', height: '18px', color: '#8c8c8c' }} />
              : <EyeIcon style={{ width: '18px', height: '18px', color: '#8c8c8c' }} />
            }
          </button>
        )}
      </div>
      {error && (
        <Text size="1" style={{ color: '#e5484d', marginTop: '4px', display: 'block' }}>
          {error}
        </Text>
      )}
    </div>
  );
};

export default Input;
